import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getOfferingById, getOfferingProducts } from '../../api/offerings';
import { createBooking, getBookingsList } from '../../api/bookings';
import { registerForEvent, getMyRegistrations, cancelEventRegistration } from '../../api/events';
import Header from '../../Components/Header/Header';
import Sidebar from '../../Components/Sidebar/Sidebar';
import '../AdventureShared.css'; // Use adventure page layout classes
import './OfferingDetailPage.css';
import ConfirmationModal from '../../Components/Shared/ConfirmationModal';
import AdventureDateTimePicker from '../../Components/Shared/AdventureDateTimePicker';
import SEO from '../../Components/SEO/SEO';
import { generateSlug } from '../../utils/slugHelper';

const CustomDropdown = ({ value, onChange, options, placeholder, disabled, hasError }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const dropdownRef = React.useRef(null);

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find(opt => opt.value === value);
  const isPlaceholder = !selectedOption;
  const displayLabel = selectedOption ? selectedOption.label : (placeholder || 'Select...');

  return (
    <div className="custom-dropdown-container" ref={dropdownRef}>
      <div
        className={`off-booking-form__input custom-dropdown-selected ${isOpen ? 'active' : ''} ${disabled ? 'disabled' : ''} ${hasError ? 'error' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
      >
        <span style={{ color: isPlaceholder ? 'rgba(255, 255, 255, 0.35)' : '#ffffff' }}>
          {displayLabel}
        </span>
        <svg
          width="10"
          height="6"
          viewBox="0 0 10 6"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
            opacity: disabled ? 0.3 : 0.7,
            color: 'rgba(255, 255, 255, 0.65)'
          }}
        >
          <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>

      {isOpen && (
        <div className="custom-dropdown-options">
          {options.map((opt) => (
            <div
              key={opt.value}
              className={`custom-dropdown-option ${opt.value === value ? 'selected' : ''}`}
              onClick={() => {
                onChange({ target: { value: opt.value } });
                setIsOpen(false);
              }}
            >
              {opt.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function OfferingDetailPage() {
  const { id, slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Derive a smart back label based on where the user came from
  const referrer = document.referrer;
  const fromMarketplace = location.key !== 'default' && (referrer.includes('/facilitate/marketplace') || referrer.includes('/adventure/marketplace') || window.history.state?.idx > 0);
  const backLabel = location.state?.fromMarketplace ? '← Back to Marketplace' : '← Back to Offerings';

  const [offering, setOffering] = useState(null);
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeGalleryImage, setActiveGalleryImage] = useState(null); // for lightbox

  // Booking Form State
  const [bookingDate, setBookingDate] = useState('');
  const [bookingTime, setBookingTime] = useState('');
  const [bookingDuration, setBookingDuration] = useState(1);
  const [bookingNotes, setBookingNotes] = useState('');
  const [bookingSubmitted, setBookingSubmitted] = useState(false);
  const [bookingError, setBookingError] = useState('');
  const [isSubmittingBooking, setIsSubmittingBooking] = useState(false);
  const [existingBooking, setExistingBooking] = useState(null);

  // Phase 5: Event specific states
  const [eventRegModalOpen, setEventRegModalOpen] = useState(false);
  const [eventTicketCount, setEventTicketCount] = useState(1);
  const [eventAttendeeNames, setEventAttendeeNames] = useState(['']);
  const [eventSpecialReqs, setEventSpecialReqs] = useState('');
  const [isSubmittingEventReg, setIsSubmittingEventReg] = useState(false);
  const [eventRegError, setEventRegError] = useState('');
  const [eventRegResult, setEventRegResult] = useState(null);
  const [userEventReg, setUserEventReg] = useState(null);
  const [countdown, setCountdown] = useState('');
  const [showCancelRegConfirm, setShowCancelRegConfirm] = useState(false);
  const [isCancellingReg, setIsCancellingReg] = useState(false);

  // Shipping Address Form State
  const [shippingAddressLine, setShippingAddressLine] = useState('');
  const [shippingCity, setShippingCity] = useState('');
  const [shippingState, setShippingState] = useState('');
  const [shippingZipcode, setShippingZipcode] = useState('');
  const [shippingCountry, setShippingCountry] = useState('');
  const [purchaseQuantity, setPurchaseQuantity] = useState(1);

  const currentUserId = localStorage.getItem("userId") || "9fa85f64-5717-4562-b3fc-2c963f66afa6";

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const data = await getOfferingById(id);

        // Draft check: if offering is draft, only allow the owner to view it
        const ownerId = data.provider?.providerId || data.providerId;
        if (data.status === 'draft' && ownerId !== currentUserId) {
          alert("This draft offering is private.");
          navigate('/facilitate/offerings');
          return;
        }

        setOffering(data);

        // Autopopulate address fields for Customer's Location services
        if ((data.deliveryType === 'in_person' || data.deliveryType === 'both') && data.address?.line1 === "Customer's Location") {
          setShippingCity(data.address.city || '');
          setShippingState(data.address.province || '');
          setShippingCountry(data.address.country || '');
          setShippingZipcode(data.address.zipcode || '');
        }

        const isEvent = data.category?.toLowerCase() === 'event';
        if (isEvent) {
          // Fetch event registrations for the customer to check if they registered
          try {
            const registrations = await getMyRegistrations();
            const activeReg = registrations.find(
              r => (r.offeringId === id || r.eventId === id || r.offering?.offeringId === id) &&
                r.status !== 'cancelled'
            );
            if (activeReg) {
              setUserEventReg(activeReg);
            }
          } catch (regErr) {
            console.error("Failed to fetch customer event registrations", regErr);
          }
        } else {
          // Fetch bookings for the customer to check if they already booked this offering
          try {
            const bookings = await getBookingsList({ role: 'customer' });
            const activeBooking = bookings.find(
              b => (b.offeringId === id || b.offering?.offeringId === id) &&
                ['pending', 'confirmed', 'in_progress', 'disputed'].includes(b.status)
            );
            if (activeBooking) {
              setExistingBooking(activeBooking);
            }
          } catch (bookingErr) {
            console.error("Failed to fetch customer bookings", bookingErr);
          }
        }

        // Fetch products for all offering categories (both events and services)
        try {
          const prods = await getOfferingProducts(id);
          setProducts(prods || []);
        } catch (prodErr) {
          console.error("Failed to fetch offering products", prodErr);
        }
      } catch (err) {
        console.error("Failed to load offering details", err);
        alert("Offering not found or error loading details.");
        navigate('/facilitate/offerings');
      } finally {
        setIsLoading(false);
      }
    };
    fetchDetails();
  }, [id, currentUserId, navigate]);

  const handleCancelEventRegAction = () => {
    setShowCancelRegConfirm(true);
  };

  const handleConfirmCancelEventReg = async () => {
    setIsCancellingReg(true);
    try {
      await cancelEventRegistration(userEventReg.registrationId || userEventReg.id);
      setUserEventReg(null);
      setEventRegResult(null);
      setShowCancelRegConfirm(false);
      // Refresh offering
      const fresh = await getOfferingById(id);
      setOffering(fresh);
    } catch (e) {
      alert("Failed to cancel registration: " + e.message);
    } finally {
      setIsCancellingReg(false);
    }
  };

  // Live Countdown Timer for events
  useEffect(() => {
    if (!offering || offering.category?.toLowerCase() !== 'event' || !offering.eventDate) return;

    const eventDateString = offering.eventDate.split('T')[0];
    const eventTimeString = offering.startTime || '00:00';
    const targetTime = new Date(`${eventDateString}T${eventTimeString}:00`).getTime();

    const timer = setInterval(() => {
      const now = new Date().getTime();
      const difference = targetTime - now;

      if (difference <= 0) {
        setCountdown("Event has started");
        clearInterval(timer);
      } else {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);
        setCountdown(`${days}d ${hours}h ${minutes}m ${seconds}s`);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [offering]);

  // Client Redirect update to canonical slugged URL
  useEffect(() => {
    if (offering) {
      const expectedSlug = generateSlug(offering.title);
      if (slug !== expectedSlug) {
        navigate(`/facilitate/offerings/${id}/${expectedSlug}`, { replace: true });
      }
    }
  }, [offering, slug, id, navigate]);

  // Pricing model display helper
  const formatPrice = () => {
    if (!offering) return '';
    const { pricingModel, price, currency } = offering;
    if (pricingModel === 'free') return 'Free';
    if (pricingModel === 'cod') return 'Cash on Delivery';

    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'USD'
    }).format(price || 0);
  };

  const getPricingModelLabel = () => {
    if (!offering) return '';
    const { pricingModel } = offering;
    if (pricingModel === 'flat') return 'flat fee';
    if (pricingModel === 'per_hour') return 'per hour';
    if (pricingModel === 'per_day') return 'per day';
    return '';
  };

  const calculateTotal = () => {
    if (!offering) return 0;
    const { price, pricingModel } = offering;
    if (pricingModel === 'free' || pricingModel === 'cod') return 0;
    if (pricingModel === 'per_hour' || pricingModel === 'per_day') {
      return (price || 0) * bookingDuration;
    }
    return price || 0; // flat
  };

  const formatTotal = (amount) => {
    if (!offering) return '';
    const { pricingModel, currency } = offering;
    if (pricingModel === 'free') return 'Free';
    if (pricingModel === 'cod') return 'Cash on Delivery';
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'USD'
    }).format(amount);
  };

  const formatCleanDate = (dateStr) => {
    if (!dateStr) return '';
    return dateStr.split('T')[0];
  };

  const simplifyErrorMessage = (msg) => {
    if (!msg || typeof msg !== 'string') return msg;
    // Replace raw ISO timestamp strings (e.g. 2025-12-31T00:00:00) with clean YYYY-MM-DD
    return msg.replace(/(\d{4}-\d{2}-\d{2})T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z?/gi, '$1');
  };

  const getMinDate = () => {
    if (offering?.availableFrom) {
      return formatCleanDate(offering.availableFrom);
    }
    return new Date().toISOString().split('T')[0];
  };

  const getMaxDate = () => {
    if (offering?.availableUntil) {
      return formatCleanDate(offering.availableUntil);
    }
    return '';
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    
    const isProduct = offering.category?.toLowerCase() === 'product';
    let scheduledAt = '';
    
    if (isProduct) {
      // Set to 5 minutes in the future to pass past check
      scheduledAt = new Date(Date.now() + 5 * 60 * 1000).toISOString();
    } else {
      if (!bookingDate || !bookingTime) {
        setBookingError('Date and Time are required.');
        return;
      }
      setBookingError('');

      // Ensure valid time string formatting (HH:mm:ss)
      let timeStr = bookingTime;
      if (timeStr && timeStr.length === 5) {
        timeStr = `${timeStr}:00`;
      }
      scheduledAt = `${bookingDate}T${timeStr}Z`;
    }

    // --- Edge Case Client Validation Checks ---
    // 1. Own offering check
    if (isOwner) {
      setBookingError("You cannot book your own offering.");
      return;
    }

    // 2. Inactive offering check
    if (offering.status && offering.status !== 'active') {
      setBookingError("Cannot book an offering that is not active.");
      return;
    }

    if (!isProduct) {
      // 3. Past UTC time check
      const scheduledDateObj = new Date(scheduledAt);
      if (isNaN(scheduledDateObj.getTime()) || scheduledDateObj < new Date()) {
        setBookingError("Cannot schedule a booking in the past.");
        return;
      }

      // 4 & 5. Availability window checks
      const availFromClean = formatCleanDate(offering.availableFrom);
      const availUntilClean = formatCleanDate(offering.availableUntil);

      if (availFromClean && bookingDate < availFromClean) {
        setBookingError(`This offering is not available before ${availFromClean}.`);
        return;
      }
      if (availUntilClean && bookingDate > availUntilClean) {
        setBookingError(`This offering is not available after ${availUntilClean}.`);
        return;
      }
    }

    const isCustomerLocationService = offering.deliveryType === 'in_person' && offering.address?.line1 === "Customer's Location";
    const hasPreShipProduct = products.some(p => p.deliveryMethod === 'pre_ship');
    const isAddressRequired = isProduct || hasPreShipProduct || isCustomerLocationService;
    if (isAddressRequired && (!shippingAddressLine.trim() || !shippingCity.trim() || !shippingState.trim() || !shippingZipcode.trim() || !shippingCountry.trim())) {
      setBookingError(isProduct
        ? 'Shipping Address is required to purchase this product.'
        : (isCustomerLocationService 
          ? 'Service Address is required because this service is delivered at your location.' 
          : 'Shipping Address is required because this offering contains a physical product to be shipped.'));
      return;
    }

    setIsSubmittingBooking(true);

    const basePriceNum = Number(offering.price || 0);
    const quantityNum = isProduct ? Number(purchaseQuantity) : 1;
    const calcTotalNum = isProduct ? basePriceNum * quantityNum : Number(calculateTotal() || 0);
    const durationNum = isProduct ? null : Number(bookingDuration || 1);

    const payload = {
      offeringId: offering.offeringId || id,
      scheduledAt,
      durationHours: durationNum,
      numberOfPeople: quantityNum,
      customerNotes: bookingNotes || "",
      shippingAddressLine: isAddressRequired ? shippingAddressLine.trim() : null,
      shippingCity: isAddressRequired ? shippingCity.trim() : null,
      shippingState: isAddressRequired ? shippingState.trim() : null,
      shippingZipcode: isAddressRequired ? shippingZipcode.trim() : null,
      shippingCountry: isAddressRequired ? shippingCountry.trim() : null,
      pricingSnapshot: {
        basePrice: basePriceNum,
        calculatedTotal: calcTotalNum,
        currency: offering.currency || 'USD'
      }
    };

    console.log("Sending booking payload:", JSON.stringify(payload, null, 2));

    try {
      const createdBooking = await createBooking(payload);
      setExistingBooking(createdBooking);
      setBookingSubmitted(true);
    } catch (err) {
      console.error("Booking request API failed:", err);
      const serverMsg = err.response?.data?.message || err.response?.data?.title || (typeof err.response?.data === 'string' ? err.response.data : null);
      setBookingError(simplifyErrorMessage(serverMsg) || 'Failed to request booking. Please check details or server connection.');
    } finally {
      setIsSubmittingBooking(false);
    }
  };

  const handleTicketCountChange = (count) => {
    setEventTicketCount(count);
    setEventAttendeeNames(prev => {
      const next = [...prev];
      if (next.length < count) {
        while (next.length < count) next.push('');
      } else if (next.length > count) {
        next.splice(count);
      }
      return next;
    });
  };

  const handleEventRegSubmit = async (e) => {
    e.preventDefault();
    if (eventAttendeeNames.some(name => !name || !name.trim())) {
      setEventRegError("Please fill in all attendee names.");
      return;
    }

    const hasPreShipProduct = products.some(p => p.deliveryMethod === 'pre_ship');
    if (hasPreShipProduct && (!shippingAddressLine.trim() || !shippingCity.trim() || !shippingState.trim() || !shippingZipcode.trim() || !shippingCountry.trim())) {
      setEventRegError('Shipping Address is required because this event contains a physical product to be shipped.');
      return;
    }

    setEventRegError('');
    setIsSubmittingEventReg(true);

    try {
      const result = await registerForEvent(id, {
        ticketCount: eventTicketCount,
        attendeeNames: eventAttendeeNames,
        specialRequirements: eventSpecialReqs,
        shippingAddressLine: hasPreShipProduct ? shippingAddressLine.trim() : null,
        shippingCity: hasPreShipProduct ? shippingCity.trim() : null,
        shippingState: hasPreShipProduct ? shippingState.trim() : null,
        shippingZipcode: hasPreShipProduct ? shippingZipcode.trim() : null,
        shippingCountry: hasPreShipProduct ? shippingCountry.trim() : null
      });
      setEventRegResult(result);
      setUserEventReg(result);

      // Refresh offering to update registeredCount
      try {
        const fresh = await getOfferingById(id);
        setOffering(fresh);
      } catch (refreshErr) {
        console.error("Failed to refresh offering details", refreshErr);
      }
    } catch (err) {
      console.error("Event registration failed:", err);
      const serverMsg = err.response?.data?.message || err.response?.data?.title || (typeof err.response?.data === 'string' ? err.response.data : null) || err.message;
      setEventRegError(simplifyErrorMessage(serverMsg) || 'Failed to register for event. Please check connection.');
    } finally {
      setIsSubmittingEventReg(false);
    }
  };

  const getMaxTicketsAllowed = () => {
    if (!offering) return 5;
    const limit = offering.maxTicketsPerPerson || 5;
    if (offering.maxCapacity !== undefined && offering.maxCapacity !== null) {
      const remaining = offering.maxCapacity - (offering.registeredCount || 0);
      return Math.max(1, Math.min(limit, remaining));
    }
    return limit;
  };

  const formatEventDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString(undefined, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatEventPrice = () => {
    if (!offering) return '';
    const { ticketPrice, pricingModel, price, currency } = offering;

    const rate = ticketPrice !== undefined && ticketPrice !== null ? ticketPrice : price;
    if (pricingModel === 'free' || rate === 0) return 'Free';

    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'USD'
    }).format(rate || 0);
  };


  if (isLoading) {
    return (
      <div className="OfferingDetailPage">
        <div className="adventurePageContainer">
          <div className="headerContain">
            <Header />
          </div>
          <div className="adventurePageContainer2">
            <div className="sidebarContain">
              <Sidebar />
            </div>
            <main className="adv-page-main animate-fade-in">
              <div className="off-detail-loader-wrap">
                <div className="off-detail-spinner"></div>
                <p>Loading offering details...</p>
              </div>
            </main>
          </div>
        </div>
      </div>
    );
  }

  if (!offering) return null;

  const allImages = [offering.coverImageUrl, ...offering.galleryImageUrls].filter(Boolean);
  const isOwner = (offering.providerId || offering.provider?.providerId) === currentUserId;

  const schemaJson = offering.category?.toLowerCase() === 'event' ? {
    "@context": "https://schema.org",
    "@type": "Event",
    "name": offering.title,
    "description": offering.description,
    "startDate": offering.eventDate ? `${offering.eventDate.split('T')[0]}T${offering.startTime || '00:00'}:00` : undefined,
    "image": offering.coverImageUrl ? [offering.coverImageUrl] : undefined,
    "location": offering.deliveryType === 'virtual' ? {
      "@type": "VirtualLocation",
      "url": window.location.href
    } : {
      "@type": "Place",
      "name": offering.address?.line1 || "Physical Venue",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": offering.address?.line1 || "",
        "addressLocality": offering.address?.city || "",
        "addressRegion": offering.address?.province || "",
        "postalCode": offering.address?.zipcode || "",
        "addressCountry": offering.address?.country || ""
      }
    },
    "offers": {
      "@type": "Offer",
      "url": window.location.href,
      "price": offering.ticketPrice || offering.price || 0,
      "priceCurrency": offering.currency || "USD",
      "availability": offering.maxCapacity && offering.registeredCount >= offering.maxCapacity ? "https://schema.org/OutOfStock" : "https://schema.org/InStock"
    },
    "organizer": {
      "@type": "Organization",
      "name": offering.provider?.name || "Organizer",
      "logo": offering.provider?.avatarUrl
    }
  } : {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": offering.title,
    "description": offering.description,
    "provider": {
      "@type": "LocalBusiness",
      "name": offering.provider?.name || "Service Provider",
      "image": offering.provider?.avatarUrl
    },
    "offers": {
      "@type": "Offer",
      "price": offering.price || 0,
      "priceCurrency": offering.currency || "USD",
      "availability": "https://schema.org/InStock"
    }
  };

  const escapedSchemaString = JSON.stringify(schemaJson).replace(/</g, '\\u003c');

  return (
    <>
      <SEO 
        title={offering.title}
        description={offering.description}
        image={offering.coverImageUrl}
        slugPath={`/facilitate/offerings/${offering.offeringId}/${offering.slug || generateSlug(offering.title)}`}
      />
      <script 
        type="application/ld+json" 
        dangerouslySetInnerHTML={{ __html: escapedSchemaString }} 
      />
      <div className="OfferingDetailPage">
        <div className="adventurePageContainer">
          <div className="headerContain">
            <Header />
          </div>
          <div className="adventurePageContainer2">
          <div className="sidebarContain">
            <Sidebar />
          </div>

          {/* Content Area */}
          <main className="adv-page-main animate-fade-in">
            <div className="off-create-header" style={{ marginBottom: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <button className="off-back-btn" onClick={() => navigate(-1)}>
                  {backLabel}
                </button>
                {isOwner && (
                  <button
                    className="off-db-btn-primary"
                    onClick={() => navigate(`/facilitate/offerings/${offering.offeringId}/edit`)}
                  >
                    Edit Offering
                  </button>
                )}
              </div>
            </div>

            {/* Hero Header Area */}
            <div className="off-detail-hero" style={{ height: '220px', paddingTop: '0px', borderRadius: '16px', marginBottom: '28px' }}>
              <img
                src={offering.coverImageUrl || "https://images.unsplash.com/photo-1531403009284-440f080d1e12?auto=format&fit=crop&w=1200&q=80"}
                alt={offering.title}
                className="off-detail-hero__bg"
              />
              <div className="off-detail-hero__overlay"></div>
              <div className="off-detail-hero__content" style={{ paddingLeft: '24px', paddingBottom: '16px' }}>
                <div className="off-detail-breadcrumb">
                  Marketplace &rarr; <span className="c-gold">{offering.category}</span> &rarr; {offering.subcategory}
                </div>
                <h2 className="off-detail-title" style={{ fontSize: '28px' }}>{offering.title}</h2>
              </div>
            </div>

            {/* Two Column Layout Container */}
            <div className="off-detail-body" style={{ padding: '0px', maxWidth: '100%' }}>
              <div className="off-detail-columns">

                {/* Left Column — Content */}
                <div className="off-detail-main">

                  {/* Description Section */}
                  <div className="off-detail-section">
                    <h2 className="off-detail-section-title">About this Offering</h2>
                    <p className="off-detail-description">{offering.description}</p>
                  </div>

                  {/* Products Included Section (Phase 6) */}
                  {products && products.length > 0 && (
                    <div className="off-detail-section animate-fade-in" style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '24px' }}>
                      <h2 className="off-detail-section-title">What's Included</h2>
                      <div className="off-detail-products-list" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '16px', marginTop: '16px' }}>
                        {products.map((prod) => (
                          <div
                            key={prod.productId || prod.id}
                            style={{
                              background: 'rgba(255,255,255,0.02)',
                              border: '1px solid rgba(255,255,255,0.06)',
                              borderRadius: '10px',
                              padding: '12px',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '8px'
                            }}
                          >
                            <div style={{ width: '100%', height: '110px', borderRadius: '6px', overflow: 'hidden', background: 'rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              {prod.imageUrl ? (
                                <img src={prod.imageUrl} alt={prod.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              ) : (
                                <span style={{ fontSize: '28px', opacity: 0.3, margin: 'auto' }}>📦</span>
                              )}
                            </div>
                            <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#ffffff' }}>{prod.name}</h4>
                            {prod.description && <p style={{ margin: 0, fontSize: '12px', color: 'rgba(255,255,255,0.6)', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: '1.4' }}>{prod.description}</p>}
                            <div style={{ marginTop: 'auto', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                              <span style={{ fontSize: '10px', background: 'rgba(72,209,204,0.1)', color: '#48d1cc', padding: '2px 6px', borderRadius: '10px', fontWeight: '600' }}>
                                {prod.deliveryMethod === 'pre_ship' ? 'Ships before appointment' :
                                  prod.deliveryMethod === 'at_event' ? 'Provided at service' :
                                    'Digital — sent on confirmation'}
                              </span>
                              {!prod.includedInPrice && (
                                <span style={{ fontSize: '10px', background: 'rgba(255,255,255,0.08)', color: '#ffffff', padding: '2px 6px', borderRadius: '10px', fontWeight: '600' }}>
                                  +${prod.addonPrice}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Media Gallery Grid */}
                  {allImages.length > 1 && (
                    <div className="off-detail-section">
                      <h2 className="off-detail-section-title">Media Gallery</h2>
                      <div className="off-detail-gallery">
                        {allImages.map((imgUrl, idx) => (
                          <div
                            key={idx}
                            className="off-detail-gallery__item"
                            onClick={() => setActiveGalleryImage(imgUrl)}
                          >
                            <img src={imgUrl} alt={`Gallery item ${idx + 1}`} className="off-detail-gallery__img" />
                            <div className="off-detail-gallery__hover"><span>🔍 View</span></div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Delivery/Location Section */}
                  <div className="off-detail-section">
                    <h2 className="off-detail-section-title">Delivery & Location</h2>
                    <div className="off-detail-location-info">
                      {offering.category?.toLowerCase() === 'product' ? (
                        <div className="off-location-card">
                          {offering.subcategory?.toLowerCase() === 'digital' ? (
                            <>
                              <span className="off-location-icon">📥</span>
                              <div>
                                <h4>Digital Download</h4>
                                <p>Instant delivery. You will receive download details after checkout.</p>
                              </div>
                            </>
                          ) : (
                            <>
                              <span className="off-location-icon">📦</span>
                              <div>
                                <h4>Physical Delivery (Shipped)</h4>
                                <p>Shipped directly to your address. Please provide shipping info at checkout.</p>
                              </div>
                            </>
                          )}
                        </div>
                      ) : (
                        <>
                          {offering.deliveryType === 'virtual' && (
                            <div className="off-location-card">
                              <span className="off-location-icon">💻</span>
                              <div>
                                <h4>Virtual Service</h4>
                                <p>Platform: {offering.virtualPlatform || 'Online Video Call'}</p>
                              </div>
                            </div>
                          )}
                          {offering.deliveryType === 'in_person' && offering.address && (
                            <div className="off-location-card">
                              <span className="off-location-icon">📍</span>
                              <div>
                                {offering.address.line1 === "Customer's Location" ? (
                                  <p>🏡 Service delivered at customer's location (Service Area: {offering.address.city}, {offering.address.province || ''} {offering.address.country} {offering.address.zipcode || ''})</p>
                                ) : (
                                  <p>{offering.address.line1}, {offering.address.city}, {offering.address.province || ''} {offering.address.country} {offering.address.zipcode || ''}</p>
                                )}
                              </div>
                            </div>
                          )}
                          {offering.deliveryType === 'both' && (
                            <div className="off-location-split">
                              <div className="off-location-card">
                                <span className="off-location-icon">📍</span>
                                <div>
                                  <h4>In-Person</h4>
                                  {offering.address?.line1 === "Customer's Location" ? (
                                    <p>🏡 Service delivered at customer's location ({offering.address?.city} {offering.address?.zipcode || ''})</p>
                                  ) : (
                                    <p>{offering.address?.line1}, {offering.address?.city}, {offering.address?.province || ''} {offering.address?.country} {offering.address?.zipcode || ''}</p>
                                  )}
                                </div>
                              </div>
                              <div className="off-location-card">
                                <span className="off-location-icon">💻</span>
                                <div>
                                  <h4>Virtual</h4>
                                  <p>Platform: {offering.virtualPlatform || 'Online Video Call'}</p>
                                </div>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Tags Section */}
                  {offering.tags && offering.tags.length > 0 && (
                    <div className="off-detail-section">
                      <div className="off-detail-tags">
                        {offering.tags.map((tag) => (
                          <span key={tag} className="off-detail-tag">#{tag}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Provider Card below fold */}
                  {offering.provider && (
                    <div className="off-detail-section off-detail-section--provider">
                      <div 
                        className="off-provider-card" 
                        onClick={() => navigate(`/facilitate/providers/${offering.provider.providerId || offering.providerId}`)}
                        style={{ cursor: 'pointer' }}
                      >
                        <img
                          src={offering.provider.avatarUrl}
                          alt={offering.provider.name}
                          className="off-provider-avatar"
                        />
                        <div className="off-provider-details">
                          <h3>Service Offered By {offering.provider.name} →</h3>
                          <p className="off-provider-bio">{offering.provider.bio}</p>
                          <span className="off-provider-date">Amalgamate member since June 2024</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Right Column — Sticky Booking Sidebar */}
                <div className="off-detail-sidebar" style={{ top: '0px' }}>
                  <div className="off-booking-card">
                    {offering.category?.toLowerCase() === 'event' ? (
                      /* ── EVENT TICKETING VIEW ── */
                      <>
                        <div className="off-booking-card__price-row">
                          <div>
                            <span className="off-booking-card__price">{formatEventPrice()}</span>
                            <span className="off-booking-card__price-label"> {offering.ticketType === 'free' ? '' : 'per ticket'}</span>
                          </div>
                          {offering.priceDescription && (
                            <span className="off-booking-card__desc">{offering.priceDescription}</span>
                          )}
                        </div>

                        <div className="off-booking-card__availability">
                          <h4>Event Date & Time</h4>
                          <p>📅 {formatEventDate(offering.eventDate)}</p>
                          <p>⏰ {offering.startTime} - {offering.endTime || 'End Time N/A'} ({offering.timezone || 'UTC'})</p>
                          {countdown && (
                            <p style={{ marginTop: '8px', fontSize: '13px', color: '#48d1cc' }}>
                              ⏳ Starts in: <strong>{countdown}</strong>
                            </p>
                          )}
                          {offering.maxCapacity && (
                            <p style={{ marginTop: '4px', fontSize: '12px', color: '#888' }}>
                              👥 Capacity: {offering.registeredCount || 0} / {offering.maxCapacity} registered
                            </p>
                          )}
                        </div>

                        <div className="off-booking-card__divider"></div>

                        {isOwner ? (
                          <div className="off-owner-box" style={{ textAlign: 'center', padding: '12px 0' }}>
                            <div style={{ fontSize: '32px', marginBottom: '8px' }}>👑</div>
                            <h4 style={{ color: '#48d1cc', margin: '0 0 6px 0', fontSize: '17px', fontWeight: '700' }}>You host this event</h4>
                            <p style={{ fontSize: '13px', color: '#aaa', lineHeight: '1.5', marginBottom: '20px' }}>
                              You are the organizer of this event. You can check the attendees log or cancel the event from the dashboard.
                            </p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                              <button
                                className="off-booking-card__btn-book"
                                onClick={() => navigate(`/facilitate/events/${offering.offeringId}/manage`)}
                              >
                                Manage Attendees 👥
                              </button>
                              <button
                                className="off-booking-card__btn-quote"
                                onClick={() => navigate(`/facilitate/offerings/${offering.offeringId}/edit`)}
                              >
                                Edit Event Details ✏️
                              </button>
                            </div>
                          </div>
                        ) : eventRegResult ? (
                          <div className="off-booking-success">
                            <div className="off-booking-success__icon" style={{ fontSize: '32px', color: '#48d1cc', textAlign: 'center', marginBottom: '12px' }}>✓</div>
                            <h4 style={{ textAlign: 'center', color: '#fff', marginBottom: '8px' }}>
                              {eventRegResult.status === 'waitlisted' ? 'Added to Waitlist!' : 'Registered!'}
                            </h4>
                            <p style={{ textAlign: 'center', fontSize: '13px', color: '#aaa', marginBottom: '16px' }}>
                              {eventRegResult.status === 'waitlisted'
                                ? `Event is currently full. Your position in queue: ${eventRegResult.waitlistPosition || 1}`
                                : `You are confirmed with ${eventRegResult.ticketCount} ticket(s).`}
                            </p>
                            <button className="off-booking-card__btn-book" onClick={() => navigate('/facilitate/home')}>
                              Go to Dashboard
                            </button>
                          </div>
                        ) : userEventReg ? (
                          <div className="off-booking-success" style={{ textAlign: 'center', padding: '12px 0' }}>
                            <div style={{ fontSize: '32px', marginBottom: '8px' }}>🎟️</div>
                            <h4 style={{ color: '#48d1cc', margin: '0 0 6px 0', fontSize: '17px', fontWeight: '700' }}>
                              {userEventReg.status === 'waitlisted' ? 'On Waitlist' : 'Registered'}
                            </h4>
                            <p style={{ fontSize: '13.5px', color: '#aaa', lineHeight: '1.5', marginBottom: '20px' }}>
                              You booked <strong>{userEventReg.ticketCount} ticket(s)</strong> for this event.
                              {userEventReg.status === 'waitlisted' && (
                                <span> (Waitlist status is active)</span>
                              )}
                            </p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                              <button
                                className="off-booking-card__btn-book"
                                onClick={handleCancelEventRegAction}
                              >
                                Cancel Registration ✕
                              </button>
                              <button
                                className="off-booking-card__btn-quote"
                                onClick={() => navigate('/facilitate/home')}
                              >
                                Go to Dashboard
                              </button>
                            </div>
                          </div>
                        ) : (
                          <form onSubmit={handleEventRegSubmit} className="off-booking-form">
                            {eventRegError && (
                              <div className="off-booking-error" style={{ color: '#ff6b6b', backgroundColor: 'rgba(255, 107, 107, 0.1)', border: '1px solid rgba(255, 107, 107, 0.3)', padding: '10px 12px', borderRadius: '8px', fontSize: '13px', lineHeight: '1.4', marginBottom: '14px' }}>
                                ⚠️ {eventRegError}
                              </div>
                            )}

                            <div className="off-booking-form__group" style={{ marginBottom: '12px' }}>
                              <label style={{ display: 'block', fontSize: '12px', color: '#aaa', marginBottom: '4px' }}>Number of Tickets *</label>
                              {getMaxTicketsAllowed() <= 10 ? (
                                <CustomDropdown
                                  value={eventTicketCount}
                                  onChange={(e) => handleTicketCountChange(parseInt(e.target.value, 10))}
                                  options={Array.from({ length: getMaxTicketsAllowed() }, (_, i) => i + 1).map((val) => ({
                                    value: val,
                                    label: `${val} ${val === 1 ? 'Ticket' : 'Tickets'}`
                                  }))}
                                  placeholder="Select tickets"
                                />
                              ) : (
                                <input
                                  type="number"
                                  min="1"
                                  max={getMaxTicketsAllowed()}
                                  value={eventTicketCount}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    if (val === '') {
                                      handleTicketCountChange('');
                                    } else {
                                      let parsed = parseInt(val, 10);
                                      if (!isNaN(parsed)) {
                                        if (parsed > getMaxTicketsAllowed()) parsed = getMaxTicketsAllowed();
                                        handleTicketCountChange(parsed);
                                      }
                                    }
                                  }}
                                  onBlur={() => {
                                    if (!eventTicketCount || eventTicketCount < 1) {
                                      handleTicketCountChange(1);
                                    }
                                  }}
                                  className="off-booking-form__input"
                                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid rgba(255,255,255,0.15)', backgroundColor: 'rgba(0,0,0,0.3)', color: '#fff' }}
                                />
                              )}
                              <span style={{ display: 'block', fontSize: '11px', color: '#888', marginTop: '4px' }}>
                                Limit: {getMaxTicketsAllowed()} ticket(s) per purchase (Remaining spots: {offering.maxCapacity ? offering.maxCapacity - (offering.registeredCount || 0) : 'Unlimited'})
                              </span>
                            </div>

                            {eventAttendeeNames.map((name, idx) => (
                              <div key={idx} className="off-booking-form__group" style={{ marginBottom: '12px' }}>
                                <label style={{ display: 'block', fontSize: '12px', color: '#aaa', marginBottom: '4px' }}>Attendee #{idx + 1} Full Name *</label>
                                <input
                                  type="text"
                                  required
                                  placeholder={`Guest ${idx + 1} Name`}
                                  value={name}
                                  onChange={(e) => {
                                    const updatedNames = [...eventAttendeeNames];
                                    updatedNames[idx] = e.target.value;
                                    setEventAttendeeNames(updatedNames);
                                  }}
                                  className="off-booking-form__input"
                                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid rgba(255,255,255,0.15)', backgroundColor: 'rgba(0,0,0,0.3)', color: '#fff' }}
                                />
                              </div>
                            ))}

                            <div className="off-booking-form__group" style={{ marginBottom: '16px' }}>
                              <label style={{ display: 'block', fontSize: '12px', color: '#aaa', marginBottom: '4px' }}>Special Requirements</label>
                              <textarea
                                maxLength="500"
                                placeholder="Dietary, accessibility, or other requirements?"
                                value={eventSpecialReqs}
                                onChange={(e) => setEventSpecialReqs(e.target.value)}
                                className="off-booking-form__textarea"
                                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid rgba(255,255,255,0.15)', backgroundColor: 'rgba(0,0,0,0.3)', color: '#fff', height: '80px', resize: 'none' }}
                              />
                              <span className="off-booking-form__char-count" style={{ display: 'block', fontSize: '10px', color: '#666', textAlign: 'right', marginTop: '2px' }}>{500 - eventSpecialReqs.length} chars remaining</span>
                            </div>

                            {products.some(p => p.deliveryMethod === 'pre_ship') && (
                              <div className="off-booking-shipping-section" style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '16px', marginTop: '16px', marginBottom: '16px' }}>
                                <h4 style={{ fontSize: '13px', color: '#48d1cc', textTransform: 'uppercase', marginBottom: '12px', fontWeight: '700' }}>Shipping Address Required</h4>

                                <div className="off-booking-form__group" style={{ marginBottom: '12px' }}>
                                  <label style={{ display: 'block', fontSize: '11px', color: '#aaa', marginBottom: '4px' }}>Street Address *</label>
                                  <input
                                    type="text"
                                    required
                                    placeholder="123 Main St, Apt 4"
                                    value={shippingAddressLine}
                                    onChange={(e) => setShippingAddressLine(e.target.value)}
                                    className="off-booking-form__input"
                                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid rgba(255,255,255,0.15)', backgroundColor: 'rgba(0,0,0,0.3)', color: '#fff' }}
                                  />
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
                                  <div className="off-booking-form__group">
                                    <label style={{ display: 'block', fontSize: '11px', color: '#aaa', marginBottom: '4px' }}>City *</label>
                                    <input
                                      type="text"
                                      required
                                      placeholder="City"
                                      value={shippingCity}
                                      onChange={(e) => setShippingCity(e.target.value)}
                                      className="off-booking-form__input"
                                      style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid rgba(255,255,255,0.15)', backgroundColor: 'rgba(0,0,0,0.3)', color: '#fff' }}
                                    />
                                  </div>
                                  <div className="off-booking-form__group">
                                    <label style={{ display: 'block', fontSize: '11px', color: '#aaa', marginBottom: '4px' }}>State / Prov *</label>
                                    <input
                                      type="text"
                                      required
                                      placeholder="State"
                                      value={shippingState}
                                      onChange={(e) => setShippingState(e.target.value)}
                                      className="off-booking-form__input"
                                      style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid rgba(255,255,255,0.15)', backgroundColor: 'rgba(0,0,0,0.3)', color: '#fff' }}
                                    />
                                  </div>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
                                  <div className="off-booking-form__group">
                                    <label style={{ display: 'block', fontSize: '11px', color: '#aaa', marginBottom: '4px' }}>Zip / Postal Code *</label>
                                    <input
                                      type="text"
                                      required
                                      placeholder="Zipcode"
                                      value={shippingZipcode}
                                      onChange={(e) => setShippingZipcode(e.target.value)}
                                      className="off-booking-form__input"
                                      style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid rgba(255,255,255,0.15)', backgroundColor: 'rgba(0,0,0,0.3)', color: '#fff' }}
                                    />
                                  </div>
                                  <div className="off-booking-form__group">
                                    <label style={{ display: 'block', fontSize: '11px', color: '#aaa', marginBottom: '4px' }}>Country *</label>
                                    <input
                                      type="text"
                                      required
                                      placeholder="Country"
                                      value={shippingCountry}
                                      onChange={(e) => setShippingCountry(e.target.value)}
                                      className="off-booking-form__input"
                                      style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid rgba(255,255,255,0.15)', backgroundColor: 'rgba(0,0,0,0.3)', color: '#fff' }}
                                    />
                                  </div>
                                </div>
                              </div>
                            )}

                            {offering.ticketType === 'paid' && (
                              <div className="off-booking-price-summary" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', borderRadius: '6px', backgroundColor: 'rgba(255,255,255,0.03)', marginBottom: '16px', border: '1px solid rgba(255,255,255,0.05)' }}>
                                <span style={{ fontSize: '13px', color: '#ccc' }}>Total Cost:</span>
                                <strong style={{ fontSize: '16px', color: '#48d1cc' }}>
                                  {new Intl.NumberFormat(undefined, {
                                    style: 'currency',
                                    currency: offering.currency || 'USD'
                                  }).format((offering.ticketPrice || 0) * eventTicketCount)}
                                </strong>
                              </div>
                            )}

                            {offering.maxCapacity && offering.registeredCount >= offering.maxCapacity ? (
                              offering.waitlistEnabled ? (
                                <button type="submit" className="off-booking-card__btn-book" disabled={isSubmittingEventReg}>
                                  {isSubmittingEventReg ? 'Joining Waitlist...' : 'Join Waitlist (Event Full)'}
                                </button>
                              ) : (
                                <button type="button" className="off-booking-card__btn-book" disabled style={{ opacity: 0.6, cursor: 'not-allowed' }}>
                                  Sold Out
                                </button>
                              )
                            ) : (
                              <button type="submit" className="off-booking-card__btn-book" disabled={isSubmittingEventReg}>
                                {isSubmittingEventReg ? 'Registering...' : 'Register for Event'}
                              </button>
                            )}
                          </form>
                        )}
                      </>
                    ) : offering.category?.toLowerCase() === 'product' ? (
                      /* ── GOODS / PRODUCT PURCHASE VIEW ── */
                      <>
                        <div className="off-booking-card__price-row">
                          <div>
                            <span className="off-booking-card__price">
                              {new Intl.NumberFormat(undefined, {
                                style: 'currency',
                                currency: offering.currency || 'USD'
                              }).format(offering.price || 0)}
                            </span>
                            <span className="off-booking-card__price-label"> per unit</span>
                          </div>
                          {offering.priceDescription && (
                            <span className="off-booking-card__desc">{offering.priceDescription}</span>
                          )}
                        </div>

                        <div className="off-booking-card__divider"></div>

                        {isOwner ? (
                          <div className="off-owner-box" style={{ textAlign: 'center', padding: '12px 0' }}>
                            <div style={{ fontSize: '32px', marginBottom: '8px' }}>👑</div>
                            <h4 style={{ color: '#48d1cc', margin: '0 0 6px 0', fontSize: '17px', fontWeight: '700' }}>This is your product listing</h4>
                            <p style={{ fontSize: '13px', color: '#aaa', lineHeight: '1.5', marginBottom: '20px' }}>
                              You are the seller of this listing. Customers can purchase this item directly.
                            </p>
                            <button
                              className="off-booking-card__btn-book"
                              onClick={() => navigate(`/facilitate/offerings/${offering.offeringId}/edit`)}
                            >
                              Edit Listing Details ✏️
                            </button>
                          </div>
                        ) : bookingSubmitted ? (
                          <div className="off-booking-success">
                            <div className="off-booking-success__icon" style={{ fontSize: '32px', color: '#48d1cc', textAlign: 'center', marginBottom: '12px' }}>✓</div>
                            <h4 style={{ textAlign: 'center', color: '#fff', marginBottom: '8px' }}>Order Submitted!</h4>
                            <p style={{ textAlign: 'center', fontSize: '13px', color: '#aaa', marginBottom: '16px' }}>Your order has been submitted successfully.</p>
                            <button 
                              className="off-booking-card__btn-book" 
                              onClick={() => navigate(`/facilitate/orders/${existingBooking?.bookingId || existingBooking?.id || ''}`)}
                            >
                              View My Order Detail
                            </button>
                          </div>
                        ) : existingBooking ? (
                          <div className="off-booking-success" style={{ textAlign: 'center', padding: '12px 0' }}>
                            <div style={{ fontSize: '32px', marginBottom: '8px' }}>📦</div>
                            <h4 style={{ color: '#48d1cc', margin: '0 0 6px 0', fontSize: '17px', fontWeight: '700' }}>Already Purchased</h4>
                            <p style={{ fontSize: '13.5px', color: '#aaa', lineHeight: '1.5', marginBottom: '20px' }}>
                              You placed an order for this item.
                            </p>
                            <button
                              className="off-booking-card__btn-book"
                              onClick={() => navigate(`/facilitate/orders/${existingBooking.bookingId || existingBooking.id}`)}
                            >
                              View Order Detail
                            </button>
                          </div>
                        ) : (
                          <form onSubmit={handleBookingSubmit} className="off-booking-form">
                            {bookingError && (
                              <div className="off-booking-error" style={{ color: '#ff6b6b', backgroundColor: 'rgba(255, 107, 107, 0.1)', border: '1px solid rgba(255, 107, 107, 0.3)', padding: '10px 12px', borderRadius: '8px', fontSize: '13px', lineHeight: '1.4', marginBottom: '14px' }}>
                                ⚠️ {bookingError}
                              </div>
                            )}

                            <div className="off-booking-form__group" style={{ marginBottom: '12px' }}>
                              <label style={{ display: 'block', fontSize: '12px', color: '#aaa', marginBottom: '4px' }}>Quantity *</label>
                              <input
                                type="number"
                                min="1"
                                max="99"
                                value={purchaseQuantity}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  if (val === '') {
                                    setPurchaseQuantity('');
                                  } else {
                                    const parsed = parseInt(val, 10);
                                    if (!isNaN(parsed)) {
                                      setPurchaseQuantity(parsed);
                                    }
                                  }
                                }}
                                onBlur={() => {
                                  if (!purchaseQuantity || purchaseQuantity < 1) {
                                    setPurchaseQuantity(1);
                                  }
                                }}
                                className="off-booking-form__input"
                                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid rgba(255,255,255,0.15)', backgroundColor: 'rgba(0,0,0,0.3)', color: '#fff' }}
                              />
                            </div>

                            <div className="off-booking-form__group" style={{ marginBottom: '16px' }}>
                              <label style={{ display: 'block', fontSize: '12px', color: '#aaa', marginBottom: '4px' }}>Special Instructions / Notes</label>
                              <textarea
                                maxLength="500"
                                placeholder="Add any special instructions for the seller..."
                                value={bookingNotes}
                                onChange={(e) => setBookingNotes(e.target.value)}
                                className="off-booking-form__textarea"
                                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid rgba(255,255,255,0.15)', backgroundColor: 'rgba(0,0,0,0.3)', color: '#fff', height: '80px', resize: 'none' }}
                              />
                            </div>

                            <div className="off-booking-shipping-section" style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '16px', marginTop: '16px', marginBottom: '16px' }}>
                              <h4 style={{ fontSize: '13px', color: '#48d1cc', textTransform: 'uppercase', marginBottom: '12px', fontWeight: '700' }}>Shipping Address</h4>

                              <div className="off-booking-form__group" style={{ marginBottom: '12px' }}>
                                <label style={{ display: 'block', fontSize: '11px', color: '#aaa', marginBottom: '4px' }}>Street Address *</label>
                                <input
                                  type="text"
                                  required
                                  placeholder="123 Main St, Apt 4"
                                  value={shippingAddressLine}
                                  onChange={(e) => setShippingAddressLine(e.target.value)}
                                  className="off-booking-form__input"
                                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid rgba(255,255,255,0.15)', backgroundColor: 'rgba(0,0,0,0.3)', color: '#fff' }}
                                />
                              </div>

                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
                                <div className="off-booking-form__group">
                                  <label style={{ display: 'block', fontSize: '11px', color: '#aaa', marginBottom: '4px' }}>City *</label>
                                  <input
                                    type="text"
                                    required
                                    placeholder="City"
                                    value={shippingCity}
                                    onChange={(e) => setShippingCity(e.target.value)}
                                    className="off-booking-form__input"
                                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid rgba(255,255,255,0.15)', backgroundColor: 'rgba(0,0,0,0.3)', color: '#fff' }}
                                  />
                                </div>
                                <div className="off-booking-form__group">
                                  <label style={{ display: 'block', fontSize: '11px', color: '#aaa', marginBottom: '4px' }}>State / Prov *</label>
                                  <input
                                    type="text"
                                    required
                                    placeholder="State"
                                    value={shippingState}
                                    onChange={(e) => setShippingState(e.target.value)}
                                    className="off-booking-form__input"
                                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid rgba(255,255,255,0.15)', backgroundColor: 'rgba(0,0,0,0.3)', color: '#fff' }}
                                  />
                                </div>
                              </div>

                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
                                <div className="off-booking-form__group">
                                  <label style={{ display: 'block', fontSize: '11px', color: '#aaa', marginBottom: '4px' }}>Zip / Postal Code *</label>
                                  <input
                                    type="text"
                                    required
                                    placeholder="Zipcode"
                                    value={shippingZipcode}
                                    onChange={(e) => setShippingZipcode(e.target.value)}
                                    className="off-booking-form__input"
                                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid rgba(255,255,255,0.15)', backgroundColor: 'rgba(0,0,0,0.3)', color: '#fff' }}
                                  />
                                </div>
                                <div className="off-booking-form__group">
                                  <label style={{ display: 'block', fontSize: '11px', color: '#aaa', marginBottom: '4px' }}>Country *</label>
                                  <input
                                    type="text"
                                    required
                                    placeholder="Country"
                                    value={shippingCountry}
                                    onChange={(e) => setShippingCountry(e.target.value)}
                                    className="off-booking-form__input"
                                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid rgba(255,255,255,0.15)', backgroundColor: 'rgba(0,0,0,0.3)', color: '#fff' }}
                                  />
                                </div>
                              </div>
                            </div>

                            <div className="off-booking-price-summary" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', borderRadius: '6px', backgroundColor: 'rgba(255,255,255,0.03)', marginBottom: '16px', border: '1px solid rgba(255,255,255,0.05)' }}>
                              <span style={{ fontSize: '13px', color: '#ccc' }}>Total Price:</span>
                              <strong style={{ fontSize: '16px', color: '#48d1cc' }}>
                                {new Intl.NumberFormat(undefined, {
                                  style: 'currency',
                                  currency: offering.currency || 'USD'
                                }).format((offering.price || 0) * purchaseQuantity)}
                              </strong>
                            </div>

                            <button type="submit" className="off-booking-card__btn-book" disabled={isSubmittingBooking}>
                              {isSubmittingBooking ? 'Ordering...' : 'Order Product'}
                            </button>
                          </form>
                        )}
                      </>
                    ) : (
                      /* ── STANDARD SERVICE BOOKING VIEW ── */
                      <>
                        <div className="off-booking-card__price-row">
                          <div>
                            <span className="off-booking-card__price">{formatPrice()}</span>
                            <span className="off-booking-card__price-label"> {getPricingModelLabel()}</span>
                          </div>
                          {offering.priceDescription && (
                            <span className="off-booking-card__desc">{offering.priceDescription}</span>
                          )}
                        </div>

                        {offering.availabilityNotes && (
                          <div className="off-booking-card__availability">
                            <h4>Schedule / Availability</h4>
                            <p>{offering.availabilityNotes}</p>
                          </div>
                        )}

                        <div className="off-booking-card__divider"></div>

                        {isOwner ? (
                          <div className="off-owner-box" style={{ textAlign: 'center', padding: '12px 0' }}>
                            <div style={{ fontSize: '32px', marginBottom: '8px' }}>👑</div>
                            <h4 style={{ color: '#48d1cc', margin: '0 0 6px 0', fontSize: '17px', fontWeight: '700' }}>This is your offering</h4>
                            <p style={{ fontSize: '13px', color: '#aaa', lineHeight: '1.5', marginBottom: '20px' }}>
                              You are the provider of this listing. Customers can view and submit booking requests for this service.
                            </p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                              <button
                                className="off-booking-card__btn-book"
                                onClick={() => navigate(`/facilitate/offerings/${offering.offeringId}/edit`)}
                              >
                                Edit Offering Details ✏️
                              </button>
                              <button
                                className="off-booking-card__btn-quote"
                                onClick={() => navigate('/facilitate/orders/inbox')}
                              >
                                View Order Requests Inbox 📬
                              </button>
                            </div>
                          </div>
                        ) : bookingSubmitted ? (
                          <div className="off-booking-success">
                            <div className="off-booking-success__icon" style={{ fontSize: '32px', color: '#48d1cc', textAlign: 'center', marginBottom: '12px' }}>✓</div>
                            <h4 style={{ textAlign: 'center', color: '#fff', marginBottom: '8px' }}>Order request sent!</h4>
                            <p style={{ textAlign: 'center', fontSize: '13px', color: '#aaa', marginBottom: '16px' }}>You'll be notified when the provider responds.</p>
                            <button 
                              className="off-booking-card__btn-book" 
                              onClick={() => navigate(`/facilitate/orders/${existingBooking?.bookingId || existingBooking?.id || ''}`)}
                            >
                              View My Order
                            </button>
                          </div>
                        ) : existingBooking ? (
                          <div className="off-booking-success" style={{ textAlign: 'center', padding: '12px 0' }}>
                            <div style={{ fontSize: '32px', marginBottom: '8px' }}>📅</div>
                            <h4 style={{ color: '#48d1cc', margin: '0 0 6px 0', fontSize: '17px', fontWeight: '700' }}>Already Booked</h4>
                            <p style={{ fontSize: '13.5px', color: '#aaa', lineHeight: '1.5', marginBottom: '20px' }}>
                              You have an active order for this service on <strong>{new Date(existingBooking.scheduledAt).toLocaleDateString()}</strong> at <strong>{new Date(existingBooking.scheduledAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</strong>.
                            </p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                              <button
                                className="off-booking-card__btn-book"
                                onClick={() => navigate(`/facilitate/orders/${existingBooking.bookingId || existingBooking.id}`)}
                              >
                                View Order Details
                              </button>
                              <button
                                className="off-booking-card__btn-quote"
                                onClick={() => navigate('/facilitate/orders')}
                              >
                                My Orders List
                              </button>
                            </div>
                          </div>
                        ) : (
                          <form onSubmit={handleBookingSubmit} className="off-booking-form">
                            {bookingError && (
                              <div className="off-booking-error" style={{ color: '#ff6b6b', backgroundColor: 'rgba(255, 107, 107, 0.1)', border: '1px solid rgba(255, 107, 107, 0.3)', padding: '10px 12px', borderRadius: '8px', fontSize: '13px', lineHeight: '1.4', marginBottom: '14px' }}>
                                ⚠️ {bookingError}
                              </div>
                            )}

                            <div className="off-booking-form__group" style={{ marginBottom: '12px' }}>
                              <AdventureDateTimePicker
                                type="date"
                                label="Date"
                                value={bookingDate}
                                onChange={setBookingDate}
                                minDate={getMinDate()}
                                maxDate={getMaxDate()}
                                required
                              />
                            </div>

                            <div className="off-booking-form__group" style={{ marginBottom: '12px' }}>
                              <AdventureDateTimePicker
                                type="time"
                                label="Time"
                                value={bookingTime}
                                onChange={setBookingTime}
                                required
                              />
                            </div>

                            {(offering.pricingModel === 'per_hour' || offering.pricingModel === 'per_day') && (
                              <div className="off-booking-form__group" style={{ marginBottom: '12px' }}>
                                <label className="off-booking-form__label">{offering.pricingModel === 'per_hour' ? 'Duration (Hours) *' : 'Duration (Days) *'}</label>
                                <select
                                  value={bookingDuration}
                                  onChange={(e) => setBookingDuration(parseInt(e.target.value, 10))}
                                  className="off-booking-form__input"
                                >
                                  {Array.from({ length: offering.pricingModel === 'per_hour' ? 12 : 30 }, (_, i) => i + 1).map((val) => (
                                    <option key={val} value={val} style={{ backgroundColor: '#0e1b1b' }}>
                                      {val} {offering.pricingModel === 'per_hour' ? (val === 1 ? 'Hour' : 'Hours') : (val === 1 ? 'Day' : 'Days')}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            )}

                            <div className="off-booking-form__group" style={{ marginBottom: '16px' }}>
                              <label className="off-booking-form__label">Notes to Provider</label>
                              <textarea
                                maxLength="500"
                                placeholder="Anything the provider should know?"
                                value={bookingNotes}
                                onChange={(e) => setBookingNotes(e.target.value)}
                                className="off-booking-form__textarea"
                              />
                              <span className="off-booking-form__char-count">{500 - bookingNotes.length} chars remaining</span>
                            </div>

                            {(products.some(p => p.deliveryMethod === 'pre_ship') || (offering.deliveryType === 'in_person' && offering.address?.line1 === "Customer's Location")) && (
                              <div className="off-booking-shipping-section" style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '16px', marginTop: '16px', marginBottom: '16px' }}>
                                <h4 style={{ fontSize: '13px', color: '#48d1cc', textTransform: 'uppercase', marginBottom: '12px', fontWeight: '700' }}>
                                  {offering.deliveryType === 'in_person' && offering.address?.line1 === "Customer's Location" ? "Service Address Required" : "Shipping Address Required"}
                                </h4>

                                <div className="off-booking-form__group" style={{ marginBottom: '12px' }}>
                                  <label style={{ display: 'block', fontSize: '11px', color: '#aaa', marginBottom: '4px' }}>Street Address *</label>
                                  <input
                                    type="text"
                                    required
                                    placeholder="123 Main St, Apt 4"
                                    value={shippingAddressLine}
                                    onChange={(e) => setShippingAddressLine(e.target.value)}
                                    className="off-booking-form__input"
                                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid rgba(255,255,255,0.15)', backgroundColor: 'rgba(0,0,0,0.3)', color: '#fff' }}
                                  />
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
                                  <div className="off-booking-form__group">
                                    <label style={{ display: 'block', fontSize: '11px', color: '#aaa', marginBottom: '4px' }}>City *</label>
                                    <input
                                      type="text"
                                      required
                                      placeholder="City"
                                      value={shippingCity}
                                      onChange={(e) => setShippingCity(e.target.value)}
                                      className="off-booking-form__input"
                                      style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid rgba(255,255,255,0.15)', backgroundColor: 'rgba(0,0,0,0.3)', color: '#fff' }}
                                    />
                                  </div>
                                  <div className="off-booking-form__group">
                                    <label style={{ display: 'block', fontSize: '11px', color: '#aaa', marginBottom: '4px' }}>State / Prov *</label>
                                    <input
                                      type="text"
                                      required
                                      placeholder="State"
                                      value={shippingState}
                                      onChange={(e) => setShippingState(e.target.value)}
                                      className="off-booking-form__input"
                                      style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid rgba(255,255,255,0.15)', backgroundColor: 'rgba(0,0,0,0.3)', color: '#fff' }}
                                    />
                                  </div>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
                                  <div className="off-booking-form__group">
                                    <label style={{ display: 'block', fontSize: '11px', color: '#aaa', marginBottom: '4px' }}>Zip / Postal Code *</label>
                                    <input
                                      type="text"
                                      required
                                      placeholder="Zipcode"
                                      value={shippingZipcode}
                                      onChange={(e) => setShippingZipcode(e.target.value)}
                                      className="off-booking-form__input"
                                      style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid rgba(255,255,255,0.15)', backgroundColor: 'rgba(0,0,0,0.3)', color: '#fff' }}
                                    />
                                  </div>
                                  <div className="off-booking-form__group">
                                    <label style={{ display: 'block', fontSize: '11px', color: '#aaa', marginBottom: '4px' }}>Country *</label>
                                    <input
                                      type="text"
                                      required
                                      placeholder="Country"
                                      value={shippingCountry}
                                      onChange={(e) => setShippingCountry(e.target.value)}
                                      className="off-booking-form__input"
                                      style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid rgba(255,255,255,0.15)', backgroundColor: 'rgba(0,0,0,0.3)', color: '#fff' }}
                                    />
                                  </div>
                                </div>
                              </div>
                            )}

                            <div className="off-booking-price-summary" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', borderRadius: '6px', backgroundColor: 'rgba(255,255,255,0.03)', marginBottom: '16px', border: '1px solid rgba(255,255,255,0.05)' }}>
                              <span style={{ fontSize: '13px', color: '#ccc' }}>Price Summary:</span>
                              <strong style={{ fontSize: '16px', color: '#48d1cc' }}>{formatTotal(calculateTotal())}</strong>
                            </div>

                            <button type="submit" className="off-booking-card__btn-book" disabled={isSubmittingBooking} style={{ opacity: isSubmittingBooking ? 0.7 : 1, cursor: isSubmittingBooking ? 'not-allowed' : 'pointer' }}>
                              {isSubmittingBooking ? 'Sending Request...' : 'Request Booking'}
                            </button>

                            <div className="off-booking-card__divider"></div>

                            <button
                              type="button"
                              className="off-booking-card__btn-quote"
                              onClick={() => navigate(`/facilitate/quotes/request/${offering.providerId || offering.provider?.providerId || 'prov-1'}`)}
                            >
                              Request a Custom Quote
                            </button>
                          </form>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>

      {/* Lightbox Overlay Portal */}
      {activeGalleryImage && (
        <div className="off-lightbox" onClick={() => setActiveGalleryImage(null)}>
          <div className="off-lightbox__content">
            <img src={activeGalleryImage} alt="Enlarged view" className="off-lightbox__img" />
            <button className="off-lightbox__close" onClick={() => setActiveGalleryImage(null)}>&times;</button>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={showCancelRegConfirm}
        onClose={() => setShowCancelRegConfirm(false)}
        onConfirm={handleConfirmCancelEventReg}
        title="Cancel Registration?"
        message="Are you sure you want to cancel your event registration?"
        confirmText="Yes, Cancel Registration"
        cancelText="Go Back"
        isDanger={true}
        isLoading={isCancellingReg}
      />
    </div>
    </>
  );
}
